import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Loader from "../components/Loader";
import s3Service from "../services/s3Service";
import "../assets/clients.css";

const ClientsEmailAll = () => {
  console.log("📧 [FRONTEND] ClientsEmailAll component rendering");
  
  const navigate = useNavigate();
  const params = useParams();
  const statusId = params.statusId || null; // Handle both /emails and /emails/status/:statusId
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    market_segment_id: "",
    selectedContacts: {},
    subject: "",
    content: "",
    files: [null], // Initialize with one empty file slot
    filesCounter: 1,
  });

  // Available data
  const [clients, setClients] = useState([]);
  const [marketSegments, setMarketSegments] = useState({});
  const [filteredClients, setFilteredClients] = useState([]);

  // Pagination for contacts table
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage, setContactsPerPage] = useState(50);

  // Form errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const url = statusId 
          ? `${backendUrl}/api/clients/emails/${statusId}`
          : `${backendUrl}/api/clients/emails`;

        console.log("📧 [FRONTEND] Fetching email data from:", url);

        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error("⏱️ [FRONTEND] Request timeout after 30 seconds");
          controller.abort();
        }, 30000); // 30 second timeout

        let response;
        try {
          response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error("Request timed out. Please check your connection and try again.");
          }
          throw fetchError;
        }

        // Check if response is ok
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}: ${response.statusText}` }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("📧 [FRONTEND] Email data response:", data);

        if (data.success && data.data) {
          setClients(data.data.clients || []);
          setFilteredClients(data.data.clients || []);
          setMarketSegments(data.data.marketSegments || {});
          
          // Pre-select all contacts by default
          const defaultSelected = {};
          (data.data.clients || []).forEach(client => {
            if (client.contacts && Array.isArray(client.contacts)) {
              client.contacts.forEach(contact => {
                defaultSelected[contact.id] = true;
              });
            }
          });
          setFormData(prev => ({
            ...prev,
            selectedContacts: defaultSelected
          }));
          
          const totalContacts = (data.data.clients || []).reduce((sum, client) => {
            return sum + (client.contacts && Array.isArray(client.contacts) ? client.contacts.length : 0);
          }, 0);
          
          console.log("✅ [FRONTEND] Email data loaded successfully:", {
            clientsCount: data.data.clients?.length || 0,
            totalContacts: totalContacts,
            marketSegmentsCount: Object.keys(data.data.marketSegments || {}).length
          });
          
          // Warn if too many contacts
          if (totalContacts > 1000) {
            console.warn(`⚠️ [FRONTEND] Large dataset detected: ${totalContacts} contacts. Consider using filters.`);
          }
        } else {
          // If API returns success:false or no data, show error but don't use mock data
          const errorMessage = data.message || "Failed to load email data";
          console.error("❌ [FRONTEND] API returned error:", errorMessage);
          setError(errorMessage);
          setClients([]);
          setFilteredClients([]);
          setMarketSegments({});
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ [FRONTEND] Error fetching email data:", err);
        let errorMessage = "Failed to load email data. Please try again.";
        
        if (err.name === 'AbortError') {
          errorMessage = "Request timed out. Please check your connection and try again.";
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setClients([]);
        setFilteredClients([]);
        setMarketSegments({});
        setLoading(false);
      }
    };

    fetchData();
  }, [statusId]);

  // Filter clients by market segment
  useEffect(() => {
    if (formData.market_segment_id === '') {
      setFilteredClients(clients);
    } else {
      const segmentId = parseInt(formData.market_segment_id);
      setFilteredClients(clients.filter(client => client.market_segment_id === segmentId));
    }
  }, [formData.market_segment_id, clients]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleContactToggle = (contactId) => {
    setFormData((prev) => ({
      ...prev,
      selectedContacts: {
        ...prev.selectedContacts,
        [contactId]: !prev.selectedContacts[contactId],
      },
    }));
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    const newSelected = {};
    filteredClients.forEach(client => {
      if (client.contacts && Array.isArray(client.contacts)) {
        client.contacts.forEach(contact => {
          if (contact && contact.id) {
            newSelected[contact.id] = checked;
          }
        });
      }
    });
    setFormData((prev) => ({
      ...prev,
      selectedContacts: newSelected,
    }));
  };

  const handleFileChange = (e, index) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      files: prev.files.map((file, i) => (i === index ? files[0] : file)),
    }));
  };

  const addFileField = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, null],
      filesCounter: prev.filesCounter + 1,
    }));
  };

  const removeFileField = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      filesCounter: prev.filesCounter - 1,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const selectedContactIds = Object.keys(formData.selectedContacts).filter(
      id => formData.selectedContacts[id]
    );

    if (selectedContactIds.length === 0) {
      newErrors.contacts = "Please select at least one contact";
    }

    if (!formData.subject) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.content) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the errors before submitting");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const selectedContactIds = Object.keys(formData.selectedContacts).filter(
        id => formData.selectedContacts[id]
      ).map(id => parseInt(id));

      // Get client IDs from selected contacts
      const clientIds = [];
      filteredClients.forEach(client => {
        if (client.contacts && Array.isArray(client.contacts)) {
          const hasSelectedContact = client.contacts.some(contact => 
            contact && contact.id && selectedContactIds.includes(contact.id)
          );
          if (hasSelectedContact) {
            clientIds.push(client.id);
          }
        }
      });

      // Upload attachments to S3 (matching Laravel tmp/ folder structure)
      const attachmentKeys = [];
      const attachmentNames = [];
      
      for (const file of formData.files) {
        if (file && file instanceof File) {
          try {
            console.log("📎 [FRONTEND] Uploading email attachment to S3:", file.name);
            // Upload to tmp/ folder matching Laravel config('site.file_tmp_path')
            const s3Key = await s3Service.uploadFileToS3(
              file,
              'tmp', // Laravel: storage_path('tmp/') maps to S3 tmp/
              null, // No progress callback
              false // Use timestamp prefix for uniqueness
            );
            attachmentKeys.push(s3Key);
            attachmentNames.push(file.name);
            console.log("✅ [FRONTEND] Attachment uploaded:", s3Key);
          } catch (uploadError) {
            console.error("❌ [FRONTEND] Failed to upload attachment:", uploadError);
            throw new Error(`Failed to upload attachment ${file.name}: ${uploadError.message}`);
          }
        }
      }

      const submitData = {
        subject: formData.subject,
        message: formData.content,
        client_ids: clientIds,
        market_segment_id: formData.market_segment_id || null,
        contacts: selectedContactIds,
        files: attachmentKeys, // S3 keys for attachments
        filesNames: attachmentNames // Original filenames
      };

      const response = await fetch(`${backendUrl}/api/clients/emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON (e.g., HTML error page), read as text
          const errorText = await response.text();
          if (errorText.includes('Payload Too Large') || response.status === 413) {
            errorMessage = "Request payload too large. Please reduce the number of selected contacts or contact support.";
          } else {
            errorMessage = errorText.substring(0, 200) || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || `Failed to send email`);
      }

      setSuccess(data.message || "You have sent the email");
      setTimeout(() => {
        navigate("/clients");
      }, 2000);
    } catch (err) {
      console.error("Error sending email:", err);
      const errorMessage = err.message || "Failed to send email";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate all contacts from filtered clients for pagination
  const allContacts = filteredClients.reduce((acc, client) => {
    if (client.contacts && Array.isArray(client.contacts)) {
      client.contacts.forEach(contact => {
        if (contact && contact.id) {
          acc.push({ ...contact, clientName: client.name, clientStatus: client.status, clientMarketSegment: client.market_segment_id });
        }
      });
    }
    return acc;
  }, []);

  // Paginate contacts
  const totalContacts = allContacts.length;
  const startIndex = (currentPage - 1) * contactsPerPage;
  const endIndex = startIndex + contactsPerPage;
  const paginatedContacts = allContacts.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalContacts / contactsPerPage);

  const allSelected = filteredClients.length > 0 && filteredClients.every(client =>
    client.contacts && Array.isArray(client.contacts) && client.contacts.every(contact => 
      contact && contact.id && formData.selectedContacts[contact.id]
    )
  );

  // Always show something, even if there's an error
  if (loading) {
    return (
      <div className="container" style={{ minHeight: '100vh' }}>
        <Loader />
      </div>
    );
  }

  // If there's an error and no clients, show error message with retry button
  if (error && clients.length === 0 && !loading) {
    return (
      <div className="container" style={{ padding: '20px', minHeight: '100vh' }}>
        <div className="breadcrumb-row mb-4">
          <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          <span className="breadcrumb-sep">/</span>
          <Link to="/clients" className="breadcrumb-users">Clients</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-users" style={{ background: '#8bc34a', color: '#fff', padding: '2px 8px', borderRadius: 3 }}>
            Emails
          </span>
        </div>
        <div className="alert alert-danger">
          <h4>Error Loading Email Form</h4>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setLoading(true);
              setError(null);
              window.location.reload();
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumbs */}
      <div className="breadcrumb-row mb-4">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <Link to="/clients" className="breadcrumb-users">Clients</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-users" style={{ background: '#8bc34a', color: '#fff', padding: '2px 8px', borderRadius: 3 }}>
          Emails
        </span>
      </div>

      <div id="prevent-multiple-requests"></div>

      {/* Display error message if data loading failed */}
      {error && !loading && (
        <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
          <strong>Error loading email data:</strong> {error}
          <br />
          <small>Please check your connection and try again, or contact support if the problem persists.</small>
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-form">
            <div className="panel-heading panel-title">
              <h3>Email clients</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit} encType="multipart/form-data" id="sendEmailForm">
                <fieldset className="form-horizontal js-email-attachments" id="js-populate-client">
                  
                  {/* Clients Section */}
                  <div className="panel-title title-standard">
                    <h3>Clients</h3>
                  </div>

                  <div className="panel-group border-down margin-down">
                    {errors.contacts && (
                      <div className="alert alert-danger">{errors.contacts}</div>
                    )}

                    <div className="mb-30">
                      <div className="form-group">
                        <label className="control-label col-sm-2">Market Segment</label>
                        <div className="col-sm-3">
                          <select
                            name="market_segment_id"
                            className="form-control js-change-market-segment"
                            value={formData.market_segment_id}
                            onChange={handleInputChange}
                          >
                            {Object.entries(marketSegments).map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="client-email-select-all-group">
                      <div className="client-email-select-all-group-text">Select All</div>
                      <input
                        type="checkbox"
                        id="select_all"
                        className="js-select-checkboxes checkbox client-email-select-all-group-input"
                        checked={allSelected}
                        onChange={handleSelectAll}
                      />
                    </div>

                    {totalContacts > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <strong>Showing {startIndex + 1}-{Math.min(endIndex, totalContacts)} of {totalContacts} contacts</strong>
                        {totalContacts > 100 && (
                          <span style={{ marginLeft: '10px', color: '#d9534f' }}>
                            (Large dataset - use filters to narrow down results)
                          </span>
                        )}
                      </div>
                    )}

                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Send email?</th>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedContacts.map((contact) => (
                          <tr
                            key={contact.id}
                            className={`js-contact-tr js-tr-market-segment-id-${contact.clientMarketSegment || 0}`}
                          >
                            <td>
                              <input
                                type="checkbox"
                                name={`contacts[${contact.id}]`}
                                id={`contacts[${contact.id}]`}
                                className={`checkbox js-contact-checkbox js-checkbox-market-segment-id-${contact.clientMarketSegment || 0}`}
                                checked={formData.selectedContacts[contact.id] || false}
                                onChange={() => handleContactToggle(contact.id)}
                              />
                            </td>
                            <td>{contact.clientName || '-'}</td>
                            <td>{contact.fullName || '-'}</td>
                            <td>{contact.email || '-'}</td>
                            <td>{contact.clientStatus || '-'}</td>
                          </tr>
                        ))}
                        {paginatedContacts.length === 0 && (
                          <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                              No contacts found. Try adjusting your filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          className="btn btn-sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage >= totalPages}
                        >
                          Next
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage >= totalPages}
                        >
                          Last
                        </button>
                        <select
                          value={contactsPerPage}
                          onChange={(e) => {
                            setContactsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          style={{ marginLeft: '10px', padding: '5px' }}
                        >
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                          <option value={200}>200 per page</option>
                        </select>
                      </div>
                    )}

                    <div className="mb-20">
                      The email confirmation will be sent only to you.
                    </div>
                  </div>

                  {/* Email Section */}
                  <div className="panel-title title-standard">
                    <h3>Email</h3>
                  </div>

                  <div className="panel-group border-down margin-down">
                    <div className="form-group">
                      <label className="control-label col-sm-2 label-required">Subject</label>
                      <div className="col-sm-10">
                        <input
                          type="text"
                          name="subject"
                          className="form-control"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                        />
                        {errors.subject && (
                          <div className="text-danger">{errors.subject}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="control-label col-sm-2 label-required">Content</label>
                      <div className="col-sm-10">
                        <textarea
                          name="content"
                          className="form-control ckeditor"
                          value={formData.content}
                          onChange={handleInputChange}
                          rows="10"
                          required
                        />
                        {errors.content && (
                          <div className="text-danger">{errors.content}</div>
                        )}
                      </div>
                    </div>

                    {/* Attachments Section */}
                    <div className="form-group">
                      <label className="control-label col-sm-2">Attachments</label>
                      <div className="col-sm-5">
                        {formData.files.map((file, index) => (
                          <div key={index} style={{ marginBottom: '10px' }}>
                            <input
                              type="file"
                              onChange={(e) => handleFileChange(e, index)}
                              className="form-control"
                              style={{ marginBottom: '5px' }}
                            />
                            {file && file instanceof File && (
                              <div style={{ 
                                padding: '5px 10px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '4px',
                                marginBottom: '5px',
                                fontSize: '14px',
                                color: '#333'
                              }}>
                                <span style={{ marginRight: '10px' }}>📎</span>
                                <strong>{file.name}</strong>
                                <span style={{ marginLeft: '10px', color: '#666', fontSize: '12px' }}>
                                  ({(file.size / 1024).toFixed(2)} KB)
                                </span>
                              </div>
                            )}
                            {formData.files.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFileField(index)}
                                className="btn btn-sm btn-danger"
                                style={{ marginTop: '5px' }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addFileField}
                          className="btn btn-sm btn-default"
                        >
                          Add File
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="form-horizontal">
                  <div className="form-group text-right">
                    <div className="col-sm-12">
                      {error && (
                        <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="alert alert-success" style={{ marginBottom: '15px' }}>
                          {success}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="btn btn-head btn-standard prevent-multiple-requests"
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send email"}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsEmailAll;

